package com.wintermar.inspection

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Boolean
import kotlin.ByteArray
import kotlin.Long
import kotlin.String
import kotlin.collections.Collection
import migrations.AnswerFileTable

public class AnswerFileTableQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> getAnswerFilesByAnswerId(answerId: String, mapper: (
    id: String,
    answerId: String,
    name: String,
    originalName: String?,
    path: String?,
    size: Long?,
    type: String?,
    blob: ByteArray?,
    uploadStatus: String?,
    hasGenerateChunk: Boolean?,
  ) -> T): Query<T> = GetAnswerFilesByAnswerIdQuery(answerId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getLong(5),
      cursor.getString(6),
      cursor.getBytes(7),
      cursor.getString(8),
      cursor.getBoolean(9)
    )
  }

  public fun getAnswerFilesByAnswerId(answerId: String): Query<AnswerFileTable> = getAnswerFilesByAnswerId(answerId, ::AnswerFileTable)

  public fun <T : Any> getAnswerFileById(id: String, mapper: (
    id: String,
    answerId: String,
    name: String,
    originalName: String?,
    path: String?,
    size: Long?,
    type: String?,
    blob: ByteArray?,
    uploadStatus: String?,
    hasGenerateChunk: Boolean?,
  ) -> T): Query<T> = GetAnswerFileByIdQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getLong(5),
      cursor.getString(6),
      cursor.getBytes(7),
      cursor.getString(8),
      cursor.getBoolean(9)
    )
  }

  public fun getAnswerFileById(id: String): Query<AnswerFileTable> = getAnswerFileById(id, ::AnswerFileTable)

  public fun <T : Any> checkGenerateChunk(id: String, mapper: (hasGenerateChunk: Boolean?) -> T): Query<T> = CheckGenerateChunkQuery(id) { cursor ->
    mapper(
      cursor.getBoolean(0)
    )
  }

  public fun checkGenerateChunk(id: String): Query<CheckGenerateChunk> = checkGenerateChunk(id, ::CheckGenerateChunk)

  /**
   * @return The number of rows updated.
   */
  public fun insertAnswerFile(
    id: String,
    answerId: String,
    name: String,
    originalName: String?,
    path: String?,
    size: Long?,
    type: String?,
    blob: ByteArray?,
    uploadStatus: String?,
    hasGenerateChunk: Boolean?,
  ): QueryResult<Long> {
    val result = driver.execute(1_781_764_849, """
        |INSERT INTO AnswerFileTable(id, answerId, name, originalName, path, size, type, blob, uploadStatus, hasGenerateChunk)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(id) DO UPDATE SET
        |   answerId=excluded.answerId,
        |   name=excluded.name,
        |   originalName=excluded.originalName,
        |   path=excluded.path,
        |   size=excluded.size,
        |   type=excluded.type,
        |   blob=excluded.blob,
        |   uploadStatus=excluded.uploadStatus,
        |   hasGenerateChunk=excluded.hasGenerateChunk
        """.trimMargin(), 10) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, answerId)
          bindString(parameterIndex++, name)
          bindString(parameterIndex++, originalName)
          bindString(parameterIndex++, path)
          bindLong(parameterIndex++, size)
          bindString(parameterIndex++, type)
          bindBytes(parameterIndex++, blob)
          bindString(parameterIndex++, uploadStatus)
          bindBoolean(parameterIndex++, hasGenerateChunk)
        }
    notifyQueries(1_781_764_849) { emit ->
      emit("AnswerFileTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun insertReplaceAnswerFile(
    id: String,
    answerId: String,
    name: String,
    originalName: String?,
    path: String?,
    size: Long?,
    type: String?,
    blob: ByteArray?,
    uploadStatus: String?,
    hasGenerateChunk: Boolean?,
  ): QueryResult<Long> {
    val result = driver.execute(-143_665_225, """
        |INSERT OR REPLACE INTO AnswerFileTable(id, answerId, name, originalName, path, size, type, blob, uploadStatus, hasGenerateChunk)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 10) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, answerId)
          bindString(parameterIndex++, name)
          bindString(parameterIndex++, originalName)
          bindString(parameterIndex++, path)
          bindLong(parameterIndex++, size)
          bindString(parameterIndex++, type)
          bindBytes(parameterIndex++, blob)
          bindString(parameterIndex++, uploadStatus)
          bindBoolean(parameterIndex++, hasGenerateChunk)
        }
    notifyQueries(-143_665_225) { emit ->
      emit("AnswerFileTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAnswerFilesByAnswerId(answerId: String): QueryResult<Long> {
    val result = driver.execute(-1_802_278_784, """DELETE FROM AnswerFileTable WHERE answerId = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, answerId)
        }
    notifyQueries(-1_802_278_784) { emit ->
      emit("AnswerFileTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAnswerFilesByAnswerIds(answerId: Collection<String>): QueryResult<Long> {
    val answerIdIndexes = createArguments(count = answerId.size)
    val result = driver.execute(null, """DELETE FROM AnswerFileTable WHERE answerId IN $answerIdIndexes""", answerId.size) {
          var parameterIndex = 0
          answerId.forEach { answerId_ ->
            bindString(parameterIndex++, answerId_)
          }
        }
    notifyQueries(-36_067_341) { emit ->
      emit("AnswerFileTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAnswerFileById(id: String): QueryResult<Long> {
    val result = driver.execute(-1_918_683_691, """DELETE FROM AnswerFileTable WHERE id = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
        }
    notifyQueries(-1_918_683_691) { emit ->
      emit("AnswerFileTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAllAnswerFiles(): QueryResult<Long> {
    val result = driver.execute(1_417_136_513, """DELETE FROM AnswerFileTable""", 0)
    notifyQueries(1_417_136_513) { emit ->
      emit("AnswerFileTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun updateUploadStatusById(uploadStatus: String?, id: String): QueryResult<Long> {
    val result = driver.execute(-432_032_468, """UPDATE AnswerFileTable SET uploadStatus = ? WHERE id = ?""", 2) {
          var parameterIndex = 0
          bindString(parameterIndex++, uploadStatus)
          bindString(parameterIndex++, id)
        }
    notifyQueries(-432_032_468) { emit ->
      emit("AnswerFileTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun updateHasGenerateChunkById(hasGenerateChunk: Boolean?, id: String): QueryResult<Long> {
    val result = driver.execute(1_887_534_167, """UPDATE AnswerFileTable SET hasGenerateChunk = ? WHERE id = ?""", 2) {
          var parameterIndex = 0
          bindBoolean(parameterIndex++, hasGenerateChunk)
          bindString(parameterIndex++, id)
        }
    notifyQueries(1_887_534_167) { emit ->
      emit("AnswerFileTable")
    }
    return result
  }

  private inner class GetAnswerFilesByAnswerIdQuery<out T : Any>(
    public val answerId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("AnswerFileTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("AnswerFileTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-2_022_666_639, """SELECT AnswerFileTable.id, AnswerFileTable.answerId, AnswerFileTable.name, AnswerFileTable.originalName, AnswerFileTable.path, AnswerFileTable.size, AnswerFileTable.type, AnswerFileTable.blob, AnswerFileTable.uploadStatus, AnswerFileTable.hasGenerateChunk FROM AnswerFileTable WHERE answerId = ? ORDER BY name ASC""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, answerId)
    }

    override fun toString(): String = "AnswerFileTable.sq:getAnswerFilesByAnswerId"
  }

  private inner class GetAnswerFileByIdQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("AnswerFileTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("AnswerFileTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(1_208_052_996, """SELECT AnswerFileTable.id, AnswerFileTable.answerId, AnswerFileTable.name, AnswerFileTable.originalName, AnswerFileTable.path, AnswerFileTable.size, AnswerFileTable.type, AnswerFileTable.blob, AnswerFileTable.uploadStatus, AnswerFileTable.hasGenerateChunk FROM AnswerFileTable WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, id)
    }

    override fun toString(): String = "AnswerFileTable.sq:getAnswerFileById"
  }

  private inner class CheckGenerateChunkQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("AnswerFileTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("AnswerFileTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-652_190_898, """SELECT hasGenerateChunk FROM AnswerFileTable WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, id)
    }

    override fun toString(): String = "AnswerFileTable.sq:checkGenerateChunk"
  }
}
